import React, { useState } from 'react';
import { CodeBracketIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface LeadEmbedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LeadEmbedModal: React.FC<LeadEmbedModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [formStyle, setFormStyle] = useState<'modern' | 'classic'>('modern');
  const [buttonColor, setButtonColor] = useState('#3B82F6');
  const [formWidth, setFormWidth] = useState('600');
  const [formTitle, setFormTitle] = useState('Érdeklődöm az ajánlatról');

  // Beágyazási kód generálása
  const generateEmbedCode = () => {
    const baseUrl = window.location.origin;
    const organizationId = user?.organizationId || 'YOUR_ORG_ID';
    
    return `<!-- YouCRM Lead Form -->
<div id="youcrm-lead-form"></div>
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${baseUrl}/embed/lead-form.js';
    script.async = true;
    script.onload = function() {
      window.YouCRMLeadForm.init({
        organizationId: '${organizationId}',
        style: '${formStyle}',
        buttonColor: '${buttonColor}',
        width: '${formWidth}px',
        title: '${formTitle}',
        language: 'hu'
      });
    };
    document.head.appendChild(script);
  })();
</script>`;
  };

  const embedCode = generateEmbedCode();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Beágyazási kód másolva!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Lead űrlap beágyazása"
      size="6xl"
    >
      <div className="p-6">
        {/* Leírás */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <CodeBracketIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Hogyan működik?
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Bal oldalon testreszabhatod az űrlapot, jobb oldalon pedig kimásolhatod a beágyazási kódot.
              </p>
            </div>
          </div>
        </div>

        {/* Két oszlopos layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bal oldal: Testreszabás + Előnézet */}
          <div className="space-y-6">
            {/* Testreszabási opciók */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Testreszabás
          </h3>

          {/* Stílus választó */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Űrlap stílus
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setFormStyle('modern')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formStyle === 'modern'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Modern</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Kerekített sarkok, árnyékok
                </div>
              </button>
              <button
                onClick={() => setFormStyle('classic')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formStyle === 'classic'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Klasszikus</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Egyszerű, letisztult
                </div>
              </button>
            </div>
          </div>

          {/* Gomb szín */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gomb színe
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={buttonColor}
                onChange={(e) => setButtonColor(e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              />
              <input
                type="text"
                value={buttonColor}
                onChange={(e) => setButtonColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                placeholder="#3B82F6"
              />
            </div>
          </div>

          {/* Űrlap szélessége */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Űrlap szélessége (px)
            </label>
            <input
              type="number"
              value={formWidth}
              onChange={(e) => setFormWidth(e.target.value)}
              min="400"
              max="1200"
              step="50"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              placeholder="600"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Ajánlott: 400-800px (mobil barát)
            </p>
          </div>

          {/* Űrlap címe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Űrlap címe
            </label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              placeholder="Érdeklődöm az ajánlatról"
            />
          </div>
        </div>

        {/* Előnézet */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Előnézet
          </h3>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 bg-gray-50 dark:bg-gray-800/50 overflow-x-auto">
            <div 
              className={`mx-auto bg-white dark:bg-gray-800 ${formStyle === 'modern' ? 'rounded-2xl shadow-xl' : 'rounded border border-gray-200 dark:border-gray-700'} p-6`}
              style={{ maxWidth: `${formWidth}px` }}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {formTitle}
              </h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Név"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  disabled
                />
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  disabled
                />
                <input
                  type="tel"
                  placeholder="Telefon"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  disabled
                />
                <input
                  type="text"
                  placeholder="Cég"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  disabled
                />
                <textarea
                  placeholder="Üzenet"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm resize-vertical"
                  disabled
                />
                <button
                  style={{ backgroundColor: buttonColor }}
                  className="w-full py-2.5 text-white rounded-lg font-medium text-sm shadow-md"
                  disabled
                >
                  Küldés
                </button>
              </div>
            </div>
          </div>
        </div>
          </div>

          {/* Jobb oldal: Beágyazási kód */}
          <div className="space-y-6">
            {/* Beágyazási kód */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Beágyazási kód
                </h3>
                <button
                  onClick={copyToClipboard}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Másolva!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Kód másolása
                    </>
                  )}
                </button>
              </div>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-96">
                  <code>{embedCode}</code>
                </pre>
              </div>
            </div>

            {/* Használati útmutató */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Használati útmutató:
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <li>Másold ki a fenti beágyazási kódot</li>
                <li>Illeszd be a weboldalad HTML kódjába, ahol meg szeretnéd jeleníteni az űrlapot</li>
                <li>Az űrlap automatikusan betöltődik és működik</li>
                <li>A beérkező lead-ek megjelennek a YouCRM Lead menüpontjában</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Bezárás gomb */}
        <div className="flex justify-end pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LeadEmbedModal;
